import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Plus, X, Trash2, TrendingDown, Check, Search, AlertTriangle } from 'lucide-react';
import { useEmpresa } from '@/context/EmpresaContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const formatPercent = (value) => {
  return `${(value || 0).toFixed(2)}%`;
};

// Formatear fecha a formato español DD/MM/AAAA
const formatDateSpanish = (dateStr) => {
  if (!dateStr) return '-';
  try {
    // Si viene en formato YYYY-MM-DD
    const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      return `${match[3]}/${match[2]}/${match[1]}`;
    }
    // Si ya está en formato DD/MM/YYYY
    if (String(dateStr).match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      return dateStr;
    }
    // Intentar parsear
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    }
    return dateStr;
  } catch {
    return dateStr;
  }
};

const emptyRow = {
  id: null,
  tipologia_producto: '',
  entidad_financiera: '',
  limite: 0,
  dispuesto: 0,
  coste_financiero: 0,
  coste_disposicion: 0,
  fecha_renovacion: '',
  isNew: true,
  isEditing: true
};

export default function OperacionesCP() {
  const { selectedEmpresa, API } = useEmpresa();
  // Empresa compartida en modo solo lectura: ocultar acciones de escritura
  const isReadOnly = selectedEmpresa?.my_role === 'read';
  const [operaciones, setOperaciones] = useState([]);
  const [tiposProducto, setTiposProducto] = useState([]);
  const [entidades, setEntidades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedOperacion, setSelectedOperacion] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [filtroEntidad, setFiltroEntidad] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroTexto, setFiltroTexto] = useState('');

  // Filtrar operaciones
  const operacionesFiltradas = useMemo(() => {
    return operaciones.filter(op => {
      if (filtroEntidad && op.entidad_financiera !== filtroEntidad) return false;
      if (filtroTipo && op.tipologia_producto !== filtroTipo) return false;
      if (filtroTexto) {
        const texto = filtroTexto.toLowerCase();
        const coincide = 
          (op.entidad_financiera || '').toLowerCase().includes(texto) ||
          (op.tipologia_producto || '').toLowerCase().includes(texto);
        if (!coincide) return false;
      }
      return true;
    });
  }, [operaciones, filtroEntidad, filtroTipo, filtroTexto]);

  // Detectar operaciones próximas a renovar (usando dias_alerta de la empresa)
  const operacionesConAlerta = useMemo(() => {
    const diasAlerta = selectedEmpresa?.dias_alerta ?? 30;
    const hoy = new Date();
    const limite = new Date();
    limite.setDate(limite.getDate() + diasAlerta);
    
    return operaciones.filter(op => {
      if (!op.fecha_renovacion) return false;
      const fechaRenov = new Date(op.fecha_renovacion);
      return fechaRenov >= hoy && fechaRenov <= limite;
    });
  }, [operaciones, selectedEmpresa?.dias_alerta]);

  const fetchData = async () => {
    if (!selectedEmpresa) {
      setLoading(false);
      return;
    }
    try {
      const [opsRes, tiposRes, entidadesRes] = await Promise.all([
        axios.get(`${API}/operaciones-cp?empresa_id=${selectedEmpresa.id}`),
        axios.get(`${API}/tipos-producto-cp?empresa_id=${selectedEmpresa.id}`),
        axios.get(`${API}/entidades-financieras?empresa_id=${selectedEmpresa.id}`)
      ]);
      setOperaciones(opsRes.data);
      setTiposProducto(tiposRes.data.filter(t => t.activo));
      setEntidades(entidadesRes.data.filter(e => e.activo));
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedEmpresa, API]);

  const handleAddRow = () => {
    if (isReadOnly) return;
    if (editingId) {
      toast.warning('Guarda o cancela la fila actual antes de añadir otra');
      return;
    }
    const newRow = { ...emptyRow, id: `new-${Date.now()}` };
    setOperaciones([newRow, ...operaciones]);
    setEditingId(newRow.id);
    setEditData(newRow);
  };

  const handleEdit = (op) => {
    if (isReadOnly) return;
    if (editingId && editingId !== op.id) {
      toast.warning('Guarda o cancela la fila actual antes de editar otra');
      return;
    }
    setEditingId(op.id);
    setEditData({ ...op });
  };

  const handleCancel = (id) => {
    if (id.toString().startsWith('new-')) {
      setOperaciones(operaciones.filter(op => op.id !== id));
    }
    setEditingId(null);
    setEditData({});
  };

  const handleSave = async () => {
    if (!editData.tipologia_producto || !editData.entidad_financiera) {
      toast.error('Tipología y Entidad son obligatorios');
      return;
    }

    // Validar fecha de renovación si se ha proporcionado
    if (editData.fecha_renovacion) {
      const fechaValida = /^\d{4}-\d{2}-\d{2}$/.test(editData.fecha_renovacion);
      if (!fechaValida) {
        toast.error('La fecha de renovación no es válida. Usa el selector de fechas.');
        return;
      }
      const fecha = new Date(editData.fecha_renovacion);
      if (isNaN(fecha.getTime())) {
        toast.error('La fecha de renovación no es válida');
        return;
      }
    }

    try {
      const payload = {
        empresa_id: selectedEmpresa.id,
        tipologia_producto: editData.tipologia_producto,
        entidad_financiera: editData.entidad_financiera,
        limite: parseFloat(editData.limite) || 0,
        dispuesto: parseFloat(editData.dispuesto) || 0,
        coste_financiero: parseFloat(editData.coste_financiero) || 0,
        coste_disposicion: parseFloat(editData.coste_disposicion) || 0,
        fecha_renovacion: editData.fecha_renovacion || ''
      };

      if (editingId.toString().startsWith('new-')) {
        await axios.post(`${API}/operaciones-cp`, payload);
        toast.success('Operación creada');
      } else {
        await axios.put(`${API}/operaciones-cp/${editingId}`, payload);
        toast.success('Operación actualizada');
      }
      
      setEditingId(null);
      setEditData({});
      fetchData();
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Error al guardar');
    }
  };

  const handleDelete = async () => {
    if (!selectedOperacion) return;
    try {
      await axios.delete(`${API}/operaciones-cp/${selectedOperacion.id}`);
      toast.success('Operación eliminada');
      setDeleteDialogOpen(false);
      setSelectedOperacion(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Error al eliminar');
    }
  };

  const openDeleteDialog = (op) => {
    setSelectedOperacion(op);
    setDeleteDialogOpen(true);
  };

  const updateEditData = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  if (!selectedEmpresa) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center" data-testid="no-empresa-cp">
        <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-4">
          <TrendingDown className="w-10 h-10 text-slate-400" />
        </div>
        <h2 className="text-2xl font-bold text-slate-800 font-['Outfit'] mb-2">
          Sin empresa seleccionada
        </h2>
        <p className="text-slate-500">
          Selecciona una empresa para gestionar las operaciones a corto plazo.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="operaciones-cp-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Outfit']">
            Operaciones Corto Plazo
          </h1>
          <p className="text-slate-500 mt-1">
            Gestiona pólizas, descuentos y financiación a corto plazo
          </p>
        </div>
        {!isReadOnly && (
          <Button
            onClick={handleAddRow}
            className="btn-primary flex items-center gap-2"
            data-testid="add-operacion-cp-btn"
          >
            <Plus className="w-4 h-4" />
            Nueva Operación
          </Button>
        )}
      </div>

      {/* Alertas de renovación */}
      {operacionesConAlerta.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="alertas-renovacion-cp">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-blue-800 mb-1">
                Operaciones próximas a renovar ({selectedEmpresa?.dias_alerta ?? 30} días)
              </h3>
              <ul className="text-sm text-blue-700 space-y-1">
                {operacionesConAlerta.map(op => (
                  <li key={op.id}>
                    <span className="font-medium">{op.entidad_financiera}</span> - {op.tipologia_producto}: 
                    renovación el <span className="font-medium">{formatDateSpanish(op.fecha_renovacion)}</span>
                    {' '}(Límite: {formatCurrency(op.limite)})
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Search className="w-4 h-4 text-slate-400" />
          <span className="text-sm font-medium text-slate-600">Filtros</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Buscar</label>
            <Input
              placeholder="Buscar por entidad o tipo..."
              value={filtroTexto}
              onChange={(e) => setFiltroTexto(e.target.value)}
              className="h-9"
              data-testid="filtro-texto-cp"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Entidad</label>
            <Select
              value={filtroEntidad}
              onValueChange={(value) => setFiltroEntidad(value === 'all' ? '' : value)}
            >
              <SelectTrigger className="h-9" data-testid="filtro-entidad-cp">
                <SelectValue placeholder="Todas las entidades" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las entidades</SelectItem>
                {entidades.map((entidad) => (
                  <SelectItem key={entidad.id} value={entidad.nombre}>
                    {entidad.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Tipo de producto</label>
            <Select
              value={filtroTipo}
              onValueChange={(value) => setFiltroTipo(value === 'all' ? '' : value)}
            >
              <SelectTrigger className="h-9" data-testid="filtro-tipo-cp">
                <SelectValue placeholder="Todos los tipos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los tipos</SelectItem>
                {tiposProducto.map((tipo) => (
                  <SelectItem key={tipo.id} value={tipo.nombre}>
                    {tipo.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {(filtroTexto || filtroEntidad || filtroTipo) && (
          <div className="mt-3 flex items-center justify-between">
            <span className="text-sm text-slate-500">
              Mostrando {operacionesFiltradas.length} de {operaciones.length} operaciones
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setFiltroTexto('');
                setFiltroEntidad('');
                setFiltroTipo('');
              }}
              className="text-slate-500 hover:text-slate-700"
            >
              Limpiar filtros
            </Button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-x-auto">
        <Table data-testid="operaciones-cp-table">
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-medium text-slate-500 min-w-[150px]">Tipología</TableHead>
              <TableHead className="font-medium text-slate-500 min-w-[150px]">Entidad</TableHead>
              <TableHead className="font-medium text-slate-500 text-right min-w-[120px]">Límite</TableHead>
              <TableHead className="font-medium text-slate-500 text-right min-w-[120px]">Dispuesto</TableHead>
              <TableHead className="font-medium text-slate-500 text-right min-w-[120px]">Disponible</TableHead>
              <TableHead className="font-medium text-slate-500 text-right min-w-[90px]">Coste Fin.</TableHead>
              <TableHead className="font-medium text-slate-500 text-right min-w-[90px]">Coste Disp.</TableHead>
              <TableHead className="font-medium text-slate-500 min-w-[120px]">Fecha Renov.</TableHead>
              <TableHead className="font-medium text-slate-500 text-center min-w-[100px]">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-10">
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
                  </div>
                </TableCell>
              </TableRow>
            ) : operacionesFiltradas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-10 text-slate-500">
                  {operaciones.length === 0 
                    ? 'No hay operaciones. Haz clic en "Nueva Operación" para añadir una.'
                    : 'No se encontraron operaciones con los filtros aplicados.'}
                </TableCell>
              </TableRow>
            ) : (
              operacionesFiltradas.map((op) => {
                const isEditing = editingId === op.id;
                const data = isEditing ? editData : op;
                const disponible = (data.limite || 0) - (data.dispuesto || 0);
                
                return (
                  <TableRow 
                    key={op.id} 
                    className={`${isEditing ? 'bg-amber-50' : 'hover:bg-slate-50'}`}
                    data-testid={`row-cp-${op.id}`}
                  >
                    <TableCell className="p-2">
                      {isEditing ? (
                        <Select
                          value={data.tipologia_producto}
                          onValueChange={(value) => updateEditData('tipologia_producto', value)}
                        >
                          <SelectTrigger className="h-9" data-testid="select-tipologia-cp">
                            <SelectValue placeholder="Seleccionar" />
                          </SelectTrigger>
                          <SelectContent>
                            {tiposProducto.map((tipo) => (
                              <SelectItem key={tipo.id} value={tipo.nombre}>
                                {tipo.nombre}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="font-medium cursor-pointer" onClick={() => handleEdit(op)}>
                          {op.tipologia_producto}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="p-2">
                      {isEditing ? (
                        <Select
                          value={data.entidad_financiera}
                          onValueChange={(value) => updateEditData('entidad_financiera', value)}
                        >
                          <SelectTrigger className="h-9" data-testid="select-entidad-cp">
                            <SelectValue placeholder="Seleccionar" />
                          </SelectTrigger>
                          <SelectContent>
                            {entidades.map((entidad) => (
                              <SelectItem key={entidad.id} value={entidad.nombre}>
                                {entidad.nombre}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="cursor-pointer" onClick={() => handleEdit(op)}>
                          {op.entidad_financiera}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="p-2 text-right">
                      {isEditing ? (
                        <Input
                          type="number"
                          className="h-9 text-right"
                          value={data.limite}
                          onChange={(e) => updateEditData('limite', e.target.value)}
                        />
                      ) : (
                        <span className="font-mono-data cursor-pointer" onClick={() => handleEdit(op)}>
                          {formatCurrency(op.limite)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="p-2 text-right">
                      {isEditing ? (
                        <Input
                          type="number"
                          className="h-9 text-right"
                          value={data.dispuesto}
                          onChange={(e) => updateEditData('dispuesto', e.target.value)}
                        />
                      ) : (
                        <span className="font-mono-data cursor-pointer" onClick={() => handleEdit(op)}>
                          {formatCurrency(op.dispuesto)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="p-2 text-right">
                      <span className="font-mono-data">
                        {formatCurrency(disponible)}
                      </span>
                    </TableCell>
                    <TableCell className="p-2 text-right">
                      {isEditing ? (
                        <Input
                          type="number"
                          step="0.01"
                          className="h-9 text-right w-20"
                          value={data.coste_financiero}
                          onChange={(e) => updateEditData('coste_financiero', e.target.value)}
                        />
                      ) : (
                        <span className="font-mono-data cursor-pointer" onClick={() => handleEdit(op)}>
                          {formatPercent(op.coste_financiero)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="p-2 text-right">
                      {isEditing ? (
                        <Input
                          type="number"
                          step="0.01"
                          className="h-9 text-right w-20"
                          value={data.coste_disposicion}
                          onChange={(e) => updateEditData('coste_disposicion', e.target.value)}
                        />
                      ) : (
                        <span className="font-mono-data cursor-pointer" onClick={() => handleEdit(op)}>
                          {formatPercent(op.coste_disposicion)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="p-2">
                      {isEditing ? (
                        <Input
                          type="date"
                          className="h-9"
                          value={data.fecha_renovacion}
                          onChange={(e) => updateEditData('fecha_renovacion', e.target.value)}
                        />
                      ) : (
                        <span className="cursor-pointer" onClick={() => handleEdit(op)}>
                          {formatDateSpanish(op.fecha_renovacion)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="p-2 text-center">
                      {isEditing ? (
                        <div className="flex justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleSave}
                            className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                            data-testid="save-cp"
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCancel(op.id)}
                            className="h-8 w-8 p-0 text-slate-500 hover:text-slate-700"
                            data-testid="cancel-cp"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex justify-center gap-1">
                          {!isReadOnly && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDeleteDialog(op)}
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-cp-${op.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-dialog-cp">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar operación?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente la operación seleccionada.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
